<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import ThemeToggle from '@/components/ThemeToggle.vue'
import BrandMark from '@/components/BrandMark.vue'
import GoogleIcon from '@/components/GoogleIcon.vue'

withDefaults(defineProps<{ showLoginButton?: boolean }>(), { showLoginButton: true })

const { currentUser, isRegistered, loginWithGoogle, loginPending } = useAuth()
const { t } = useI18n()

async function handleLogin() {
  try {
    await loginWithGoogle()
  } catch (e: any) {
    if (e.code !== 'auth/cancelled-popup-request' && e.code !== 'auth/popup-closed-by-user') {
      console.error('Login failed:', e)
    }
  }
}
</script>

<template>
  <nav class="navbar">
    <div class="navbar-inner">
      <BrandMark to="/" />

      <div class="navbar-links">
        <router-link to="/how-it-works" class="nav-link hide-xs">{{ t('nav.howItWorks') }}</router-link>
        <router-link to="/pricing" class="nav-link">{{ t('nav.pricing') }}</router-link>
        <router-link to="/legal" class="nav-link hide-sm">{{ t('nav.legal') }}</router-link>
      </div>

      <div class="navbar-actions">
        <LanguageSwitcher />
        <ThemeToggle />
        <router-link v-if="currentUser" to="/files" class="ui-btn ui-btn-ghost ui-btn-sm hide-sm">
          <span class="mdi mdi-folder-outline"></span>
          {{ t('nav.files') }}
        </router-link>
        <button
          v-if="!isRegistered && showLoginButton"
          class="ui-btn ui-btn-ghost ui-btn-sm hide-sm"
          :disabled="loginPending"
          @click="handleLogin"
        >
          <GoogleIcon :size="14" />
          {{ t('nav.signInGoogle') }}
        </button>
        <router-link to="/upload" class="ui-btn ui-btn-primary ui-btn-sm">
          <span class="mdi mdi-plus"></span>
          {{ t('nav.newUpload') }}
        </router-link>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--nav-bg);
  backdrop-filter: saturate(160%) blur(14px);
  -webkit-backdrop-filter: saturate(160%) blur(14px);
  border-bottom: 1px solid var(--border);
}

.navbar-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.25rem;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.navbar-links {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex: 1;
}

.nav-link {
  color: var(--text-muted);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: var(--text);
  background: var(--surface-hover);
}

.navbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 480px) {
  .hide-xs {
    display: none;
  }
}

@media (max-width: 768px) {
  .navbar-inner {
    gap: 0.75rem;
  }

  .hide-sm {
    display: none;
  }
}
</style>
